import { useState } from 'react';
import {
  Building2,
  LogIn,
  Monitor,
  Smartphone,
  ArrowUpRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { EmptyState } from '@/components/ui/EmptyState';
import { QueryError } from '@/components/ui/QueryError';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { Tabs } from '@/components/ui/Tabs';
import { activityApi } from '@/features/activity/api/activity.api';
import { USAGE_PERIODS, type UsagePeriod } from '@/features/activity/constants';
import { useApiQuery } from '@/hooks/useApiQuery';
import { formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { UsageWindow } from '@/types/usage';

const AVATAR_COLORS = [
  'bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:text-blue-400',
  'bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-400',
  'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400',
  'bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-400',
  'bg-purple-100 text-purple-700 dark:bg-purple-950/60 dark:text-purple-400',
];

function percentOf(part: number, whole: number): number {
  return whole === 0 ? 0 : Math.round((part / whole) * 100);
}

export function LoginsPanel() {
  const [period, setPeriod] = useState<UsagePeriod>('7');
  const days = Number(period) as UsageWindow;

  const { data, isLoading, error, refetch } = useApiQuery(
    () => activityApi.logins(days),
    [days],
  );

  return (
    <section
      className="flex flex-col justify-between rounded-2xl border border-slate-200 dark:border-slate-700/80 bg-white dark:bg-slate-900 p-4 sm:p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
      aria-labelledby="logins-heading"
    >
      <div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <LogIn className="h-4 w-4 text-[#5b52f9]" />
            <h2
              id="logins-heading"
              className="text-[14.5px] font-bold text-slate-800 dark:text-slate-100 tracking-tight"
            >
              Login breakdown
            </h2>
          </div>
          <Tabs
            options={USAGE_PERIODS}
            value={period}
            onChange={setPeriod}
            label="Login period"
            variant="segmented"
          />
        </div>

        {isLoading && <SkeletonLines count={5} />}

        {!isLoading && error && (
          <QueryError title="Could not load logins" error={error} onRetry={refetch} />
        )}

        {!isLoading && !error && data && data.totals.logins === 0 && (
          <EmptyState
            icon={<LogIn className="h-6 w-6" />}
            title="No sign-ins in this period"
            description="Nobody from a school or college has signed in yet. Try the longer period."
          />
        )}

        {!isLoading && !error && data && data.totals.logins > 0 && (
          <div className="mt-4 flex flex-col gap-3">
            {/* Top 3 Metric Strip */}
            <div className="grid grid-cols-3 gap-2.5">
              {/* Box 1: Total logins */}
              <div className="rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-gradient-to-b from-white to-slate-50/70 dark:from-slate-900 dark:to-slate-800/30 p-3 shadow-[0_2px_8px_rgba(0,0,0,0.03)] transition-all hover:shadow-xs">
                <span className="text-[12px] font-semibold text-slate-700 dark:text-slate-400">Total logins</span>
                <p className="mt-0.5 text-[22px] font-black text-slate-900 dark:text-white tabular-nums leading-tight">
                  {formatNumber(data.totals.logins)}
                </p>
                <p className="text-[11px] text-slate-400 dark:text-slate-500 font-medium">
                  {formatNumber(data.totals.people)} people
                </p>
              </div>

              {/* Box 2: Web */}
              <div className="rounded-2xl border border-indigo-100 dark:border-indigo-900/50 bg-gradient-to-b from-white to-indigo-50/60 dark:from-slate-900 dark:to-indigo-950/20 p-3 shadow-[0_2px_8px_rgba(99,102,241,0.05)] transition-all hover:shadow-xs">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1 text-[11.5px] font-bold text-slate-700 dark:text-slate-300">
                    <Monitor className="h-3 w-3 text-[#5b52f9]" />
                    <span>Web</span>
                  </span>
                  <span className="text-[11px] font-black text-[#5b52f9]">
                    {percentOf(data.totals.web, data.totals.logins)}%
                  </span>
                </div>
                <p className="mt-0.5 text-[22px] font-black text-slate-900 dark:text-white tabular-nums leading-tight">
                  {formatNumber(data.totals.web)}
                </p>
                <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-indigo-100 dark:bg-slate-700">
                  <div
                    className="h-full rounded-full bg-[#5b52f9]"
                    style={{ width: `${percentOf(data.totals.web, data.totals.logins)}%` }}
                  />
                </div>
              </div>

              {/* Box 3: App */}
              <div className="rounded-2xl border border-emerald-100 dark:border-emerald-900/50 bg-gradient-to-b from-white to-emerald-50/60 dark:from-slate-900 dark:to-emerald-950/20 p-3 shadow-[0_2px_8px_rgba(16,185,129,0.05)] transition-all hover:shadow-xs">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1 text-[11.5px] font-bold text-slate-700 dark:text-slate-300">
                    <Smartphone className="h-3 w-3 text-emerald-600" />
                    <span>App</span>
                  </span>
                  <span className="text-[11px] font-black text-emerald-600">
                    {percentOf(data.totals.app, data.totals.logins)}%
                  </span>
                </div>
                <p className="mt-0.5 text-[22px] font-black text-slate-900 dark:text-white tabular-nums leading-tight">
                  {formatNumber(data.totals.app)}
                </p>
                <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-emerald-100 dark:bg-slate-700">
                  <div
                    className="h-full rounded-full bg-emerald-500"
                    style={{ width: `${percentOf(data.totals.app, data.totals.logins)}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Table of institutes */}
            {data.institutes.length > 0 && (
              <div className="overflow-x-auto rounded-xl border border-slate-100 dark:border-slate-800 max-h-[220px]">
                <table className="w-full text-left text-[12px]">
                  <thead className="bg-slate-50/90 dark:bg-slate-800/80 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider text-[11px] sticky top-0">
                    <tr>
                      <th className="px-3 py-2">Institute</th>
                      <th className="px-3 py-2 text-right">Logins</th>
                      <th className="px-3 py-2 text-right">Web</th>
                      <th className="px-3 py-2 text-right">App</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {data.institutes.map((row, idx) => {
                      const avatarColor = AVATAR_COLORS[idx % AVATAR_COLORS.length];
                      return (
                        <tr
                          key={row.uuid}
                          className="hover:bg-slate-50/60 dark:hover:bg-slate-800/40 transition-colors"
                        >
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <span
                                className={cn(
                                  'grid h-6 w-6 shrink-0 place-items-center rounded-md text-[10px] font-bold',
                                  avatarColor,
                                )}
                              >
                                <Building2 className="h-3 w-3" />
                              </span>
                              <Link
                                to={`/platform/institutes/${row.uuid}`}
                                className="font-bold text-slate-800 dark:text-slate-200 hover:text-[#5b52f9] truncate max-w-[140px]"
                              >
                                {row.displayName}
                              </Link>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right font-black text-slate-900 dark:text-white tabular-nums">
                            {formatNumber(row.logins)}
                          </td>
                          <td className="px-3 py-2 text-right font-medium text-slate-600 dark:text-slate-300 tabular-nums">
                            {formatNumber(row.web)}
                          </td>
                          <td className="px-3 py-2 text-right font-medium text-slate-600 dark:text-slate-300 tabular-nums">
                            {formatNumber(row.app)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
        <Link
          to="/platform/institutes"
          className="inline-flex items-center gap-1 text-[12.5px] font-bold text-[#5b52f9] hover:underline transition-colors group"
        >
          <span>View institutes</span>
          <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
      </div>
    </section>
  );
}
